/**
 * Handing a search from the command bar to the leads page.
 *
 * Carried in the router's navigation state rather than the URL: the free
 * text is often a name, an email address or a phone number, and a URL ends
 * up in history, in logs and in links people paste. The page applies it once
 * and clears it, so a reload or Back does not apply it again.
 */
import type { LeadFilters } from '../../api/queries';

export type LeadsHandoff = {
  /** The filters to show, replacing the open segment's own. */
  filters: LeadFilters;
  /** Free text for the page's search box. */
  search: string;
  /** Open Save as segment straight away. */
  saveAs?: boolean;
  /**
   * A search with OR (or `repeats:`), as typed. The filter panel cannot show
   * either, so the page runs `filters` (which carry `any_of` or
   * `repeats_min`) as they are and shows this in a read-only "Search:"
   * banner instead.
   */
  query?: string;
};

export type HandoffState = { cmdk?: LeadsHandoff } | null;

export function handoffFrom(state: unknown): LeadsHandoff | null {
  const h = (state as HandoffState)?.cmdk;
  if (!h || typeof h !== 'object' || typeof h.filters !== 'object' || h.filters === null) return null;
  return {
    filters: h.filters,
    search: typeof h.search === 'string' ? h.search : '',
    saveAs: h.saveAs === true,
    ...(typeof h.query === 'string' ? { query: h.query } : {}),
  };
}

/** True for a handed-over search the filter panel cannot show: OR, or `repeats:`. */
export function isOrHandoff(h: LeadsHandoff): boolean {
  return (Array.isArray(h.filters.any_of) && h.filters.any_of.length > 0) || h.filters.repeats_min != null;
}
